import { useMutation } from '@tanstack/react-query'
import { AudioLines, BrainCircuit, Clock, FileAudio, Mic, User } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { useLocalStorage } from 'usehooks-ts'
import { $fetch, useFetch } from 'utils/fetch'
import { WavStreamPlayer } from 'wavtools'
import { create } from 'zustand'

export const supportedEffects = [
  {
    name: 'laugh',
    color: '#e8faf0',
    textColor: '#052814',
    description: 'Insert a laughing sound',
  },
  {
    name: 'excited',
    color: '#fefce8',
    textColor: '#312107',
    description: 'Speak with excitement',
  },
  {
    name: 'whisper',
    color: '#e6f1fe',
    textColor: '#001731',
    description: 'Speak in a whisper',
  },
  {
    name: 'barking',
    color: '#fee7ef',
    textColor: '#310413',
    description: 'Insert barking sound',
  },
  {
    name: 'sad',
    color: '#f2eafa',
    textColor: '#180828',
    description: 'Speak with sadness',
  },
]

export const guidelines = [
  {
    icon: Clock,
    text: 'Provide 1-2 high-quality audio samples for optimal cloning results. Max length of 1 minute and 10MB each file, 25MB max total.',
  },
  {
    icon: Mic,
    text: 'Record in a quiet space with high-fidelity audio - clean audio yields better clones',
  },
  {
    icon: AudioLines,
    text: 'Ensure consistent audio levels and speaking style across samples',
  },
  {
    icon: User,
    text: 'Use audio with one clear voice and minimal background noise (less than 20% non-voice sounds)',
  },
  {
    icon: FileAudio,
    text: 'Submit high-fidelity WAV files for best results - the quality of your samples directly impacts clone quality',
  },
  {
    icon: BrainCircuit,
    text: 'Our LLM-based system can learn expressiveness, style variations, and even unique speech patterns',
  },
]

export const useStudioStore = create(set => ({
  selectedCloneId: null,
  setSelectedCloneId: id => set({ selectedCloneId: id }),

  // Generation history stored per clone
  generationHistories: {},

  // Add generation to history for a specific clone
  addGeneration: (cloneId, generation) => set((state) => {
    const currentHistory = state.generationHistories[cloneId] || []

    // If we're at the limit, remove oldest entries and clean up their blob URLs
    if (currentHistory.length >= 50) {
      const removedItems = currentHistory.slice(49)
      removedItems.forEach((item) => {
        if (item.audioDataURL) {
          URL.revokeObjectURL(item.audioDataURL)
        }
      })
    }

    return {
      generationHistories: {
        ...state.generationHistories,
        [cloneId]: [
          generation,
          ...currentHistory,
        ].slice(0, 50), // Keep only last 50 generations per clone
      },
    }
  }),

  // Clear history for a specific clone
  clearGenerationHistory: cloneId => set((state) => {
    // Clean up blob URLs before clearing
    const history = state.generationHistories[cloneId] || []
    history.forEach((item) => {
      if (item.audioDataURL) {
        URL.revokeObjectURL(item.audioDataURL)
      }
    })

    return {
      generationHistories: {
        ...state.generationHistories,
        [cloneId]: [],
      },
    }
  }),
}))

function useScript({ setIsPlaying, expressiveness, consistency, mode, audioRef, abortControllerRef }) {
  const selectedCloneId = useStudioStore(state => state.selectedCloneId)
  const addGeneration = useStudioStore(state => state.addGeneration)
  const [testScript] = useLocalStorage('testScript', 'Hey it\'s Blandie. How are you doing today?')

  return useMutation({
    mutationFn: async () => {
      if (mode === 'listen') {
        // Create abort controller for this request
        const abortController = new AbortController()
        abortControllerRef.current = abortController

        // Use streaming for listen mode
        const response = await $fetch(`/v1/voices/speak`, {
          method: 'POST',
          body: {
            text: testScript,
            voice_id: selectedCloneId,
            expressiveness,
            consistency,
            stream: true, // Enable streaming
          },
          responseType: 'stream',
          signal: abortController.signal,
        })

        return response
      }
      else {
        // Use blob response for download mode
        return $fetch(`/v1/voices/speak`, {
          method: 'POST',
          body: {
            text: testScript,
            voice_id: selectedCloneId,
            expressiveness,
            consistency,
          },
          responseType: 'blob',
        })
      }
    },
    onSuccess: async (data) => {
      if (mode === 'listen') {
        // Handle streaming audio
        const reader = data.getReader()
        let player = null
        let isCancelled = false

        try {
          // Initialize WavStreamPlayer with 24000 Hz sample rate (matching backend)
          // Note: Backend outputs at 44100Hz for web platform by default
          player = new WavStreamPlayer({ sampleRate: 44100 })
          await player.connect()

          // Store player reference for cleanup
          audioRef.current = player

          // Only set playing after successful connection
          setIsPlaying(true)

          // Accumulate chunks for blob creation
          const audioChunks = []
          const rawChunks = [] // Keep raw chunks for blob creation
          let isFirstChunk = true

          // Buffer to handle incomplete PCM16 samples across chunk boundaries
          let remainderBuffer = null

          // Read and play chunks
          while (true) {
            const { done, value } = await reader.read()

            if (done)
              break

            // Check if we've been cancelled
            if (!audioRef.current || audioRef.current !== player) {
              isCancelled = true
              break
            }

            // Keep raw chunk for blob creation
            rawChunks.push(value)

            // Skip WAV header in first chunk (44 bytes typically)
            let audioData = value
            if (isFirstChunk) {
              // Check for RIFF header
              const riffString = String.fromCharCode(...new Uint8Array(value.buffer, value.byteOffset, 4))

              if (riffString === 'RIFF') {
                // Skip standard WAV header (44 bytes)
                audioData = value.slice(44)
              }
              isFirstChunk = false
            }

            // Handle PCM16 alignment - combine with any remainder from previous chunk
            let processData = audioData
            if (remainderBuffer) {
              // Combine remainder with new data
              const combined = new Uint8Array(remainderBuffer.length + audioData.length)
              combined.set(remainderBuffer, 0)
              combined.set(audioData, remainderBuffer.length)
              processData = combined
              remainderBuffer = null
            }

            // Check if we have an odd number of bytes
            let alignedData = processData
            if (processData.length % 2 !== 0) {
              // Save the last byte for the next chunk
              remainderBuffer = new Uint8Array([processData[processData.length - 1]])
              // Use only the even-aligned portion
              alignedData = processData.slice(0, processData.length - 1)
            }

            // Accumulate aligned chunks for processing
            audioChunks.push(alignedData)

            // Only process audio if not cancelled and we have data
            if (!isCancelled && audioRef.current === player && alignedData.length > 0) {
              try {
                // Ensure we have a properly aligned buffer for Int16Array
                let int16Data
                if (alignedData.byteOffset % 2 === 0) {
                  // Byte offset is aligned, can create Int16Array directly
                  int16Data = new Int16Array(
                    alignedData.buffer,
                    alignedData.byteOffset,
                    alignedData.byteLength / 2,
                  )
                }
                else {
                  // Byte offset not aligned, need to copy to new aligned buffer
                  const alignedBuffer = new ArrayBuffer(alignedData.length)
                  new Uint8Array(alignedBuffer).set(alignedData)
                  int16Data = new Int16Array(alignedBuffer)
                }

                // Add audio to player queue
                player.add16BitPCM(int16Data, `${selectedCloneId}-${Date.now()}`)
              }
              catch (error) {
                // Ignore disconnected port errors when cancelled
                if (!error.message.includes('disconnected port')) {
                  throw error
                }
              }
            }
          }

          // Handle any remaining byte (shouldn't happen with proper audio, but just in case)
          if (remainderBuffer && !isCancelled) {
            console.warn('Streaming ended with incomplete PCM16 sample')
          }

          // Only create blob and save to history if not cancelled
          if (!isCancelled && rawChunks.length > 0) {
            // Create blob URL from raw chunks (preserving WAV header)
            const fullRawData = new Uint8Array(
              rawChunks.reduce((acc, chunk) => acc + chunk.byteLength, 0),
            )
            let offset = 0
            for (const chunk of rawChunks) {
              fullRawData.set(chunk, offset)
              offset += chunk.byteLength
            }

            // Extract just the audio data (skip header) for duration calculation
            let audioDataForDuration = fullRawData
            if (fullRawData.length > 4) {
              const headerCheck = String.fromCharCode(...fullRawData.slice(0, 4))
              if (headerCheck === 'RIFF') {
                audioDataForDuration = fullRawData.slice(44)
              }
            }

            // The raw data already includes WAV header from server, so just create blob
            const wavBlob = new Blob([fullRawData], { type: 'audio/wav' })
            const audioDataURL = URL.createObjectURL(wavBlob)

            // Estimate playback duration based on actual audio data
            // PCM16 at 44100Hz = 88200 bytes per second
            const estimatedDuration = (audioDataForDuration.byteLength / 88200) * 1000
            const waitTime = Math.max(estimatedDuration, 500) // More accurate timing

            // Wait for playback to complete before saving to history
            await new Promise((resolve) => {
              const timeout = setTimeout(() => {
                resolve()
              }, waitTime)

              // Also resolve early if player is interrupted
              const checkInterval = setInterval(() => {
                if (!audioRef.current || audioRef.current !== player) {
                  clearInterval(checkInterval)
                  clearTimeout(timeout)
                  resolve()
                }
              }, 100)
            })

            // Save to generation history only after playback completes
            if (!isCancelled && selectedCloneId) {
              const newestGen = {
                id: crypto.randomUUID(),
                audioDataURL,
                text: testScript,
                expressiveness,
                consistency,
                timestamp: Date.now(),
              }
              addGeneration(selectedCloneId, newestGen)
            }
          }
        }
        catch (error) {
          // Check if it's an abort error
          if (error.name !== 'AbortError') {
            console.error('Streaming error:', error)
            throw error
          }
        }
        finally {
          // Clean up
          reader.releaseLock()
          if (player && audioRef.current === player) {
            try {
              player.interrupt()
            }
            catch {
              // Ignore errors during cleanup
            }
            audioRef.current = null
          }
          setIsPlaying(false)
          if (abortControllerRef) {
            abortControllerRef.current = null
          }
        }
      }
      else if (mode === 'download') {
        // Handle download
        const audioUrl = URL.createObjectURL(data)
        const a = document.createElement('a')
        a.href = audioUrl
        a.download = `${selectedCloneId}-${Date.now()}.wav`
        a.click()
        URL.revokeObjectURL(audioUrl)
      }
    },
    onError: (error) => {
      console.error('error', error)

      // Don't show error toast for aborted requests
      if (error.name === 'AbortError') {
        return
      }

      let errorMessage = 'An error occurred while downloading the sample audio'
      if (mode === 'listen') {
        setIsPlaying(false)
        if (audioRef.current) {
          if (audioRef.current instanceof WavStreamPlayer) {
            try {
              audioRef.current.interrupt()
            }
            catch {
              // Ignore errors during cleanup
            }
          }
          audioRef.current = null
        }
        errorMessage = 'An error occurred while playing the sample audio'
      }
      toast.error(errorMessage)
    },
  })
}

export function useTestScript(expressiveness, consistency) {
  const [isPlaying, setIsPlaying] = useState(false)
  const audioRef = useRef(null)
  const abortControllerRef = useRef(null)

  const playMutation = useScript({
    setIsPlaying,
    expressiveness,
    consistency,
    mode: 'listen',
    audioRef,
    abortControllerRef,
  })

  const { mutate: downloadScript, isPending: isDownloading } = useScript({
    expressiveness,
    consistency,
    mode: 'download',
  })

  const pauseScript = useCallback(() => {
    // Immediately update state
    setIsPlaying(false)

    // Abort the HTTP request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }

    // Interrupt the player
    if (audioRef.current) {
      // Check if it's a WavStreamPlayer instance
      if (audioRef.current instanceof WavStreamPlayer) {
        try {
          audioRef.current.interrupt()
        }
        catch {
          // Ignore errors during cleanup
        }
        audioRef.current = null
      }
      else {
        // Legacy Audio element support
        audioRef.current.pause()
        audioRef.current = null
      }
    }
  }, [])

  return {
    isPlaying,
    playScript: playMutation.mutate,
    pauseScript,
    isGenerating: playMutation.isPending,
    downloadScript,
    isDownloading,
  }
}

export function useSamples(cloneId) {
  return useFetch(`/v1/voices/${cloneId}/samples`, {
    queryKey: ['samples', cloneId],
    dataKey: ['samples'],
    enabled: !!cloneId,
    staleTime: Infinity,
  })
}
